import React, { useState, memo } from 'react';
import { UserPlus, FileText, Calendar, Mail, CheckCircle, AlertCircle, ChevronRight, ChevronDown, FileCheck, Users, MessageSquare, Clock, Info, User, Bot, Briefcase, Search, CheckSquare, Bell, CalendarDays, Send, ThumbsUp, Eye, Edit, RefreshCw, Settings, BarChart2, GitBranch, Activity, X, Save, ArrowLeft } from 'lucide-react';
const translations = {
  en: {
    title: 'Workflow Automation',
    subtitle: 'Streamline your hiring process with automated workflows',
    workflows: 'Available Workflows',
    newCandidate: 'New Candidate Processing',
    interviewScheduling: 'Interview Scheduling',
    documentVerification: 'Document Verification',
    followUp: 'Candidate Follow-up',
    configure: 'Configure',
    activate: 'Activate',
    deactivate: 'Deactivate',
    steps: 'Workflow Steps',
    metrics: 'Performance Metrics',
    avgTime: 'Avg. Time Saved',
    accuracy: 'Accuracy Rate',
    satisfaction: 'User Satisfaction',
    details: 'View Details',
    status: {
      active: 'Active',
      inactive: 'Inactive'
    },
    stepStatus: {
      completed: 'Completed',
      inProgress: 'In Progress',
      pending: 'Pending',
      failed: 'Failed'
    },
    stepDetails: {
      view: 'View Details',
      edit: 'Edit Step',
      timeEstimate: 'Est. Time:',
      aiAssistance: 'AI Assistance:',
      integration: 'Integration:',
      detailsTitle: 'Step Details',
      editTitle: 'Edit Step',
      close: 'Close',
      save: 'Save Changes',
      cancel: 'Cancel',
      description: 'Description',
      status: 'Status',
      icon: 'Icon',
      integrations: 'Integrations',
      dependencies: 'Dependencies',
      nextSteps: 'Next Steps',
      previousRuns: 'Previous Runs',
      lastRun: 'Last Run',
      averageTime: 'Average Time',
      successRate: 'Success Rate',
      back: 'Back to Step'
    },
    newCandidateSteps: [{
      name: 'Receive application',
      description: 'System receives and logs new candidate application from all sources (website, job boards, email)',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Email, Job Boards',
      icon: 'FileText'
    }, {
      name: 'Parse CV and extract skills',
      description: 'AI automatically extracts key information from CV including skills, experience, education and contact details',
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Document Parser',
      icon: 'Search'
    }, {
      name: 'Match with job requirements',
      description: 'System compares candidate skills and experience with job requirements to determine fit score',
      status: 'inProgress',
      timeEstimate: '2-3 mins',
      aiAssistance: 'High',
      integration: 'ATS System',
      icon: 'CheckSquare'
    }, {
      name: 'Score candidate fit',
      description: 'Algorithm generates comprehensive candidate score based on qualifications, experience and job requirements',
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Scoring Engine',
      icon: 'BarChart2'
    }, {
      name: 'Notify recruiter',
      description: 'System alerts recruiter about new candidate with qualification summary and recommended next steps',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Medium',
      integration: 'Email, Slack',
      icon: 'Bell'
    }],
    interviewSteps: [{
      name: 'Check calendar availability',
      description: 'System automatically checks calendars of all interviewers to identify available time slots',
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Medium',
      integration: 'Google Calendar, Outlook',
      icon: 'CalendarDays'
    }, {
      name: 'Suggest time slots',
      description: 'AI suggests optimal interview times based on participant availability and preferences',
      status: 'completed',
      timeEstimate: '1 min',
      aiAssistance: 'High',
      integration: 'Scheduling Engine',
      icon: 'Clock'
    }, {
      name: 'Send calendar invites',
      description: 'System automatically sends calendar invitations to all participants with meeting details',
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email, Calendar',
      icon: 'Send'
    }, {
      name: 'Send confirmation emails',
      description: 'Personalized confirmation emails sent to candidates with interview details and preparation materials',
      status: 'pending',
      timeEstimate: '1 min',
      aiAssistance: 'Medium',
      integration: 'Email System',
      icon: 'Mail'
    }, {
      name: 'Set up reminders',
      description: 'Automated reminders scheduled for all participants 24 hours and 1 hour before interview',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Notification System',
      icon: 'Bell'
    }],
    documentVerificationSteps: [{
      name: 'Request documents',
      description: 'System automatically requests required verification documents from candidates',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email System',
      icon: 'FileText'
    }, {
      name: 'Receive and classify documents',
      description: 'AI categorizes and organizes submitted documents by type and completeness',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Document Management',
      icon: 'FileCheck'
    }, {
      name: 'Verify authenticity',
      description: 'System checks documents for authenticity and validates information against databases',
      status: 'pending',
      timeEstimate: '3-5 mins',
      aiAssistance: 'Medium',
      integration: 'Verification APIs',
      icon: 'CheckCircle'
    }, {
      name: 'Flag discrepancies',
      description: 'AI identifies and highlights any inconsistencies or missing information',
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Fraud Detection',
      icon: 'AlertCircle'
    }, {
      name: 'Update candidate record',
      description: 'System updates candidate profile with verification status and document information',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'ATS System',
      icon: 'RefreshCw'
    }],
    followUpSteps: [{
      name: 'Monitor candidate status',
      description: 'System tracks candidate progress through hiring pipeline and identifies stalled applications',
      status: 'completed',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'ATS System',
      icon: 'Eye'
    }, {
      name: 'Generate personalized messages',
      description: 'AI creates customized follow-up messages based on candidate status and previous interactions',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Content Engine',
      icon: 'MessageSquare'
    }, {
      name: 'Schedule follow-up communications',
      description: 'System determines optimal timing for follow-up messages to maximize engagement',
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Scheduling System',
      icon: 'Calendar'
    }, {
      name: 'Send communications',
      description: 'Automated delivery of follow-up messages through email, SMS, or other channels',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Communication APIs',
      icon: 'Send'
    }, {
      name: 'Track response and engagement',
      description: 'System monitors candidate responses and engagement with follow-up communications',
      status: 'pending',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'Analytics Engine',
      icon: 'Activity'
    }]
  },
  de: {
    title: 'Workflow-Automatisierung',
    subtitle: 'Optimieren Sie Ihren Einstellungsprozess mit automatisierten Workflows',
    workflows: 'Verfügbare Workflows',
    newCandidate: 'Neue Kandidatenverarbeitung',
    interviewScheduling: 'Terminplanung für Vorstellungsgespräche',
    documentVerification: 'Dokumentenprüfung',
    followUp: 'Kandidaten-Nachverfolgung',
    configure: 'Konfigurieren',
    activate: 'Aktivieren',
    deactivate: 'Deaktivieren',
    steps: 'Workflow-Schritte',
    metrics: 'Leistungskennzahlen',
    avgTime: 'Durchschn. Zeitersparnis',
    accuracy: 'Genauigkeitsrate',
    satisfaction: 'Benutzerzufriedenheit',
    details: 'Details anzeigen',
    status: {
      active: 'Aktiv',
      inactive: 'Inaktiv'
    },
    stepStatus: {
      completed: 'Abgeschlossen',
      inProgress: 'In Bearbeitung',
      pending: 'Ausstehend',
      failed: 'Fehlgeschlagen'
    },
    stepDetails: {
      view: 'Details anzeigen',
      edit: 'Schritt bearbeiten',
      timeEstimate: 'Geschätzte Zeit:',
      aiAssistance: 'KI-Unterstützung:',
      integration: 'Integration:',
      detailsTitle: 'Schrittdetails',
      editTitle: 'Schritt bearbeiten',
      close: 'Schließen',
      save: 'Änderungen speichern',
      cancel: 'Abbrechen',
      description: 'Beschreibung',
      status: 'Status',
      icon: 'Symbol',
      integrations: 'Integrationen',
      dependencies: 'Abhängigkeiten',
      nextSteps: 'Nächste Schritte',
      previousRuns: 'Vorherige Ausführungen',
      lastRun: 'Letzte Ausführung',
      averageTime: 'Durchschnittliche Zeit',
      successRate: 'Erfolgsrate',
      back: 'Zurück zum Schritt'
    },
    newCandidateSteps: [{
      name: 'Bewerbung erhalten',
      description: 'System erhält und protokolliert neue Kandidateneinladungen aus allen Quellen (Website, Jobboards, E-Mail)',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Email, Jobboards',
      icon: 'FileText'
    }, {
      name: 'Lebenslauf analysieren und Fähigkeiten extrahieren',
      description: 'AI extrahiert aus Lebensläufen wichtige Informationen wie Fähigkeiten, Erfahrung, Bildung und Kontaktdaten',
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Document Parser',
      icon: 'Search'
    }, {
      name: 'Mit Jobanforderungen abgleichen',
      description: 'System vergleicht Kandidateneigenschaften mit Jobanforderungen um Fit-Score zu bestimmen',
      status: 'inProgress',
      timeEstimate: '2-3 mins',
      aiAssistance: 'High',
      integration: 'ATS System',
      icon: 'CheckSquare'
    }, {
      name: 'Kandidateneignung bewerten',
      description: 'Algorithmus generiert umfassende Kandidateneignungsbewertung basierend auf Qualifikationen, Erfahrung und Jobanforderungen',
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Scoring Engine',
      icon: 'BarChart2'
    }, {
      name: 'Recruiter benachrichtigen',
      description: 'System benachrichtigt den Recruiter über neue Kandidaten mit Qualifikationszusammenfassung und empfohlenen nächsten Schritten',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Medium',
      integration: 'Email, Slack',
      icon: 'Bell'
    }],
    interviewSteps: [{
      name: 'Kalenderverfügbarkeit prüfen',
      description: 'System überprüft Kalender aller Interviewer um mögliche Zeitfenster zu identifizieren',
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Medium',
      integration: 'Google Calendar, Outlook',
      icon: 'CalendarDays'
    }, {
      name: 'Zeitfenster vorschlagen',
      description: 'AI schlägt optimale Interviewzeiten basierend auf Teilnehmerfreundlichkeit und Präferenzen vor',
      status: 'completed',
      timeEstimate: '1 min',
      aiAssistance: 'High',
      integration: 'Scheduling Engine',
      icon: 'Clock'
    }, {
      name: 'Kalendereinladungen senden',
      description: 'System sendet automatisch Kalendereinladungen an alle Teilnehmer mit Termindetails',
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email, Calendar',
      icon: 'Send'
    }, {
      name: 'Bestätigungsmails senden',
      description: 'Persönliche Bestätigungsmails an Kandidaten mit Interviewdetails und Vorbereitungsmaterialien',
      status: 'pending',
      timeEstimate: '1 min',
      aiAssistance: 'Medium',
      integration: 'Email System',
      icon: 'Mail'
    }, {
      name: 'Erinnerungen einrichten',
      description: 'Automatische Erinnerungen für alle Teilnehmer in 24 Stunden und 1 Stunde vor dem Interview',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Notification System',
      icon: 'Bell'
    }],
    documentVerificationSteps: [{
      name: 'Dokumente anfordern',
      description: 'System fordert erforderliche Prüfungsdocumente von Kandidaten automatisch an',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email System',
      icon: 'FileText'
    }, {
      name: 'Dokumente erhalten und klassifizieren',
      description: 'AI sortiert und ordnet eingereichte Dokumente nach Typ und Vollständigkeit',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Document Management',
      icon: 'FileCheck'
    }, {
      name: 'Authentizität überprüfen',
      description: 'System überprüft Dokumente auf Authentizität und validiert Informationen gegen Datenbanken',
      status: 'pending',
      timeEstimate: '3-5 mins',
      aiAssistance: 'Medium',
      integration: 'Verification APIs',
      icon: 'CheckCircle'
    }, {
      name: 'Fehlzeichen markieren',
      description: 'AI identifiziert und hervorhebt alle Konflikte oder fehlende Informationen',
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Fraud Detection',
      icon: 'AlertCircle'
    }, {
      name: 'Kandidatendaten aktualisieren',
      description: 'System aktualisiert Kandidatendaten mit Prüfungsstatus und Dokumentinformationen',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'ATS System',
      icon: 'RefreshCw'
    }],
    followUpSteps: [{
      name: 'Kandidatenstatus überwachen',
      description: 'System überwacht Kandidatenfortschritt durch Stellenbewerbspipeline und identifiziert verwaiste Anträge',
      status: 'completed',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'ATS System',
      icon: 'Eye'
    }, {
      name: 'Personalisierte Nachrichten generieren',
      description: 'AI erstellt anpassende Nachverfolgungsnachrichten basierend auf Kandidatenstatus und vorherigen Interaktionen',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Content Engine',
      icon: 'MessageSquare'
    }, {
      name: 'Nachverfolgungsmeldungen planen',
      description: 'System bestimmt optimale Zeitpunkt für Nachverfolgungsnachrichten um Engagement zu maximieren',
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Scheduling System',
      icon: 'Calendar'
    }, {
      name: 'Nachverfolgungsmeldungen senden',
      description: 'Automatische Übermittlung von Nachverfolgungsnachrichten über E-Mail, SMS oder andere Kanäle',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Communication APIs',
      icon: 'Send'
    }, {
      name: 'Antworten und Engagement überwachen',
      description: 'System überwacht Kandidatenantworten und Engagement mit Nachverfolgungsnachrichten',
      status: 'pending',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'Analytics Engine',
      icon: 'Activity'
    }]
  },
  fr: {
    title: 'Automatisation des flux de travail',
    subtitle: 'Rationalisez votre processus de recrutement avec des flux de travail automatizzés',
    workflows: 'Flux de travail disponibles',
    newCandidate: 'Traitement des nouveaux candidats',
    interviewScheduling: "Planification d'entretiens",
    documentVerification: 'Vérification des documents',
    followUp: 'Suivi des candidats',
    configure: 'Configurer',
    activate: 'Activer',
    deactivate: 'Désactiver',
    steps: 'Étapes du flux de travail',
    metrics: 'Métriques de performance',
    avgTime: 'Temps moyen économisé',
    accuracy: 'Taux de précision',
    satisfaction: 'Satisfaction utilisateur',
    details: 'Voir les détails',
    status: {
      active: 'Actif',
      inactive: 'Inactif'
    },
    stepStatus: {
      completed: 'Terminé',
      inProgress: 'En cours',
      pending: 'En attente',
      failed: 'Échoué'
    },
    stepDetails: {
      view: 'Voir les détails',
      edit: "Modifier l'étape",
      timeEstimate: 'Temps estimé:',
      aiAssistance: 'Assistance IA:',
      integration: 'Intégration:',
      detailsTitle: "Détails de l'étape",
      editTitle: "Modifier l'étape",
      close: 'Fermer',
      save: 'Enregistrer les modifications',
      cancel: 'Annuler',
      description: 'Description',
      status: 'Statut',
      icon: 'Icône',
      integrations: 'Intégrations',
      dependencies: 'Dépendances',
      nextSteps: 'Étapes suivantes',
      previousRuns: 'Exécutions précédentes',
      lastRun: 'Dernière exécution',
      averageTime: 'Temps moyen',
      successRate: 'Taux de réussite',
      back: "Retour à l'étape"
    },
    newCandidateSteps: [{
      name: 'Recevoir la candidature',
      description: 'System reçoit et logue les candidatures nouvelles de toutes les sources (site, job boards, email)',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Email, Job Boards',
      icon: 'FileText'
    }, {
      name: 'Analyser le CV et extraire les compétences',
      description: "AI extrait automatiquement les informations clés du CV, y compris les compétences, l'expérience, l'éducation et les détails de contact",
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Document Parser',
      icon: 'Search'
    }, {
      name: 'Correspondre aux exigences du poste',
      description: "System compare les compétences et l'expérience du candidat aux exigences du poste pour déterminer le score de correspondance",
      status: 'inProgress',
      timeEstimate: '2-3 mins',
      aiAssistance: 'High',
      integration: 'ATS System',
      icon: 'CheckSquare'
    }, {
      name: "Évaluer l'adéquation du candidat",
      description: "Algorithmus génère une évaluation complète du candidat basée sur les qualifications, l'expérience et les exigences du poste",
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Scoring Engine',
      icon: 'BarChart2'
    }, {
      name: 'Notifier le recruteur',
      description: 'System alerte le recruteur sur les nouvelles candidatures avec une résumé de qualifications et les étapes suivantes recommandées',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Medium',
      integration: 'Email, Slack',
      icon: 'Bell'
    }],
    interviewSteps: [{
      name: 'Vérifier la disponibilité du calendrier',
      description: 'System vérifie automatiquement les calendriers des tous les intervieweurs pour identifier les créneaux disponibles',
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Medium',
      integration: 'Google Calendar, Outlook',
      icon: 'CalendarDays'
    }, {
      name: 'Suggérer des créneaux horaires',
      description: "AI suggère les meilleures heures d'entretiens en fonction de la disponibilité des participants et de leurs préférences",
      status: 'completed',
      timeEstimate: '1 min',
      aiAssistance: 'High',
      integration: 'Scheduling Engine',
      icon: 'Clock'
    }, {
      name: 'Envoyer des invitations calendrier',
      description: 'System envoie automatiquement les invitations de calendrier à tous les participants avec les détails de la réunion',
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email, Calendar',
      icon: 'Send'
    }, {
      name: 'Envoyer des e-mails de confirmation',
      description: "E-mails de confirmation personnalisés envoyés aux candidats avec les détails de l'entretien et les matériels de préparation",
      status: 'pending',
      timeEstimate: '1 min',
      aiAssistance: 'Medium',
      integration: 'Email System',
      icon: 'Mail'
    }, {
      name: 'Configurer des rappels',
      description: "Rappels automatiques programmés pour tous les participants 24 heures et 1 heure avant l'entretien",
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Notification System',
      icon: 'Bell'
    }],
    documentVerificationSteps: [{
      name: 'Demander des documents',
      description: 'System demande automatiquement les documents de vérification requis aux candidats',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email System',
      icon: 'FileText'
    }, {
      name: 'Recevoir et classer les documents',
      description: 'AI classe et organise les documents soumis par type et complétude',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Document Management',
      icon: 'FileCheck'
    }, {
      name: "Vérifier l'authenticité",
      description: "System vérifie les documents pour l'authenticité et valide les informations contre des bases de données",
      status: 'pending',
      timeEstimate: '3-5 mins',
      aiAssistance: 'Medium',
      integration: 'Verification APIs',
      icon: 'CheckCircle'
    }, {
      name: 'Marquer les différences',
      description: 'AI identifie et souligne toutes les incohérences ou informations manquantes',
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Fraud Detection',
      icon: 'AlertCircle'
    }, {
      name: 'Mettre à jour le profil du candidat',
      description: 'System met à jour le profil du candidat avec le statut de vérification et les informations de document',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'ATS System',
      icon: 'RefreshCw'
    }],
    followUpSteps: [{
      name: 'Surveiller le statut du candidat',
      description: 'System suit le progrès du candidat dans la chaîne de recrutement et identifie les candidatures bloquées',
      status: 'completed',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'ATS System',
      icon: 'Eye'
    }, {
      name: 'Générer des messages personnalisés',
      description: 'AI crée des messages de suivi personnalisés basés sur le statut du candidat et les interactions précédentes',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Content Engine',
      icon: 'MessageSquare'
    }, {
      name: 'Planifier les communications de suivi',
      description: "System détermine le meilleur moment pour les messages de suivi pour maximiser l'engagement",
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Scheduling System',
      icon: 'Calendar'
    }, {
      name: 'Envoyer les communications',
      description: "Automatisation de l'envoi des messages de suivi par e-mail, SMS ou autres canaux",
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Communication APIs',
      icon: 'Send'
    }, {
      name: "Surveiller les réponses et l'engagement",
      description: "System surveille les réponses des candidats et l'engagement avec les messages de suivi",
      status: 'pending',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'Analytics Engine',
      icon: 'Activity'
    }]
  },
  it: {
    title: 'Automazione del flusso di lavoro',
    subtitle: 'Ottimizza il tuo processo di assunzione con flussi di lavoro automatizzati',
    workflows: 'Flussi di lavoro disponibili',
    newCandidate: 'Elaborazione nuovi candidati',
    interviewScheduling: 'Pianificazione colloqui',
    documentVerification: 'Verifica documenti',
    followUp: 'Follow-up candidati',
    configure: 'Configura',
    activate: 'Attiva',
    deactivate: 'Disattiva',
    steps: 'Fasi del flusso di lavoro',
    metrics: 'Metriche di prestazione',
    avgTime: 'Tempo medio risparmiato',
    accuracy: 'Tasso di precisione',
    satisfaction: 'Soddisfazione utente',
    details: 'Visualizza dettagli',
    status: {
      active: 'Attivo',
      inactive: 'Inattivo'
    },
    stepStatus: {
      completed: 'Completato',
      inProgress: 'In corso',
      pending: 'In attesa',
      failed: 'Fallito'
    },
    stepDetails: {
      view: 'Visualizza dettagli',
      edit: 'Modifica fase',
      timeEstimate: 'Tempo stimato:',
      aiAssistance: 'Assistenza AI:',
      integration: 'Integrazione:',
      detailsTitle: 'Dettagli fase',
      editTitle: 'Modifica fase',
      close: 'Chiudi',
      save: 'Salva modifiche',
      cancel: 'Annulla',
      description: 'Descrizione',
      status: 'Stato',
      icon: 'Icona',
      integrations: 'Integrazioni',
      dependencies: 'Dipendenze',
      nextSteps: 'Fasi successive',
      previousRuns: 'Esecuzioni precedenti',
      lastRun: 'Ultima esecuzione',
      averageTime: 'Tempo medio',
      successRate: 'Tasso di successo',
      back: 'Torna alla fase'
    },
    newCandidateSteps: [{
      name: 'Ricevere la candidatura',
      description: 'System riceve e registra le candidature nuove da tutte le fonti (sito, job boards, email)',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Email, Job Boards',
      icon: 'FileText'
    }, {
      name: 'Analizzare il CV ed estrarre le competenze',
      description: "AI estrae automaticamente le informazioni chiave dal CV, tra cui le competenze, l'esperienza, l'educazione e i dettagli di contatto",
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Document Parser',
      icon: 'Search'
    }, {
      name: 'Confrontare con i requisiti del lavoro',
      description: "System confronta le competenze e l'esperienza del candidato con i requisiti del lavoro per determinare il punteggio di adeguatezza",
      status: 'inProgress',
      timeEstimate: '2-3 mins',
      aiAssistance: 'High',
      integration: 'ATS System',
      icon: 'CheckSquare'
    }, {
      name: "Valutare l'idoneità del candidato",
      description: "Algoritmo genera una valutazione completa del candidato basata sulle qualificazioni, l'esperienza e i requisiti del lavoro",
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Scoring Engine',
      icon: 'BarChart2'
    }, {
      name: 'Notificare il recruiter',
      description: 'System avvisa il recruiter sulle nuove candidature con un riassunto delle qualificazioni e le prossime azioni consigliate',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Medium',
      integration: 'Email, Slack',
      icon: 'Bell'
    }],
    interviewSteps: [{
      name: 'Controllare la disponibilità del calendario',
      description: 'System controlla automaticamente i calendari di tutti gli intervistatori per identificare i slot di tempo disponibili',
      status: 'completed',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Medium',
      integration: 'Google Calendar, Outlook',
      icon: 'CalendarDays'
    }, {
      name: 'Suggerire fasce orarie',
      description: 'AI suggerisce le migliori ore di intervista basandosi sulla disponibilità dei partecipanti e le loro preferenze',
      status: 'completed',
      timeEstimate: '1 min',
      aiAssistance: 'High',
      integration: 'Scheduling Engine',
      icon: 'Clock'
    }, {
      name: 'Inviare inviti calendario',
      description: 'System invia automaticamente gli inviti di calendario a tutti i partecipanti con i dettagli della riunione',
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email, Calendar',
      icon: 'Send'
    }, {
      name: 'Inviare email di conferma',
      description: "E-mail di conferma personalizzate inviate ai candidati con i dettagli dell'intervista e materiali di preparazione",
      status: 'pending',
      timeEstimate: '1 min',
      aiAssistance: 'Medium',
      integration: 'Email System',
      icon: 'Mail'
    }, {
      name: 'Impostare promemoria',
      description: "Rappelli automatici programmati per tutti i partecipanti in 24 ore e 1 ora prima dell'intervista",
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Notification System',
      icon: 'Bell'
    }],
    documentVerificationSteps: [{
      name: 'Richiedere documenti',
      description: 'System richiede automaticamente i documenti di verifica necessari ai candidati',
      status: 'completed',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Email System',
      icon: 'FileText'
    }, {
      name: 'Ricevere e classificare documenti',
      description: 'AI classifica e organizza i documenti inviati in base al tipo e alla complessità',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Document Management',
      icon: 'FileCheck'
    }, {
      name: "Vérifier l'authenticité",
      description: "System vérifie les documents pour l'authenticité et valide les informations contre des bases de données",
      status: 'pending',
      timeEstimate: '3-5 mins',
      aiAssistance: 'Medium',
      integration: 'Verification APIs',
      icon: 'CheckCircle'
    }, {
      name: 'Marquer les différences',
      description: 'AI identifie et souligne toutes les incohérences ou informations manquantes',
      status: 'pending',
      timeEstimate: '1-2 mins',
      aiAssistance: 'High',
      integration: 'Fraud Detection',
      icon: 'AlertCircle'
    }, {
      name: 'Mettre à jour le profil du candidat',
      description: 'System met à jour le profil du candidat avec le statut de vérification et les informations de document',
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'ATS System',
      icon: 'RefreshCw'
    }],
    followUpSteps: [{
      name: 'Monitorare lo stato del candidato',
      description: 'System monitora lo stato del candidato attraverso la pipeline di recrutamento e identifica le candidature bloccate',
      status: 'completed',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'ATS System',
      icon: 'Eye'
    }, {
      name: 'Generare messaggi personalizzati',
      description: 'AI crea messaggi di seguito personalizzati basati sullo stato del candidato e le interazioni precedenti',
      status: 'inProgress',
      timeEstimate: '1-2 mins',
      aiAssistance: 'Very High',
      integration: 'Content Engine',
      icon: 'MessageSquare'
    }, {
      name: 'Programmare le comunicazioni di seguito',
      description: "System determina il momento ottimale per i messaggi di seguito per massimizzare l'engagement",
      status: 'inProgress',
      timeEstimate: '< 1 min',
      aiAssistance: 'High',
      integration: 'Scheduling System',
      icon: 'Calendar'
    }, {
      name: 'Inviare comunicazioni',
      description: "Automazione dell'invio dei messaggi di seguito tramite e-mail, SMS o altri canali",
      status: 'pending',
      timeEstimate: '< 1 min',
      aiAssistance: 'Low',
      integration: 'Communication APIs',
      icon: 'Send'
    }, {
      name: "Traccia le risposte e l'engagement",
      description: "System traccia le risposte dei candidati e l'engagement con le comunicazioni di seguito",
      status: 'pending',
      timeEstimate: 'Ongoing',
      aiAssistance: 'Medium',
      integration: 'Analytics Engine',
      icon: 'Activity'
    }]
  }
};
export function WorkflowAutomation({
  language
}) {
  const t = translations[language];
  const [activeWorkflow, setActiveWorkflow] = useState('newCandidate');
  const [workflowStatus, setWorkflowStatus] = useState({
    newCandidate: true,
    interviewScheduling: true,
    documentVerification: false,
    followUp: true
  });
  const [expandedStep, setExpandedStep] = useState(null);
  const [viewingStepDetails, setViewingStepDetails] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const toggleWorkflowStatus = workflow => {
    setWorkflowStatus({
      ...workflowStatus,
      [workflow]: !workflowStatus[workflow]
    });
  };
  const getWorkflowIcon = workflow => {
    switch (workflow) {
      case 'newCandidate':
        return <UserPlus className="text-blue-500" data-id="element-375" />;
      case 'interviewScheduling':
        return <Calendar className="text-green-500" data-id="element-376" />;
      case 'documentVerification':
        return <FileText className="text-purple-500" data-id="element-377" />;
      case 'followUp':
        return <Mail className="text-orange-500" data-id="element-378" />;
      default:
        return null;
    }
  };
  const getStepIcon = (iconName, status) => {
    const iconColor = status === 'completed' ? 'text-green-500' : status === 'inProgress' ? 'text-blue-500' : status === 'failed' ? 'text-red-500' : 'text-gray-500';
    const iconProps = {
      size: 18,
      className: iconColor
    };
    switch (iconName) {
      case 'FileText':
        return <FileText {...iconProps} data-id="element-379" />;
      case 'Search':
        return <Search {...iconProps} data-id="element-380" />;
      case 'CheckSquare':
        return <CheckSquare {...iconProps} data-id="element-381" />;
      case 'BarChart2':
        return <BarChart2 {...iconProps} data-id="element-382" />;
      case 'Bell':
        return <Bell {...iconProps} data-id="element-383" />;
      case 'CalendarDays':
        return <CalendarDays {...iconProps} data-id="element-384" />;
      case 'Clock':
        return <Clock {...iconProps} data-id="element-385" />;
      case 'Send':
        return <Send {...iconProps} data-id="element-386" />;
      case 'Mail':
        return <Mail {...iconProps} data-id="element-387" />;
      case 'FileCheck':
        return <FileCheck {...iconProps} data-id="element-388" />;
      case 'CheckCircle':
        return <CheckCircle {...iconProps} data-id="element-389" />;
      case 'AlertCircle':
        return <AlertCircle {...iconProps} data-id="element-390" />;
      case 'RefreshCw':
        return <RefreshCw {...iconProps} data-id="element-391" />;
      case 'Eye':
        return <Eye {...iconProps} data-id="element-392" />;
      case 'MessageSquare':
        return <MessageSquare {...iconProps} data-id="element-393" />;
      case 'Calendar':
        return <Calendar {...iconProps} data-id="element-394" />;
      case 'Activity':
        return <Activity {...iconProps} data-id="element-395" />;
      default:
        return <Info {...iconProps} data-id="element-396" />;
    }
  };
  const getWorkflowSteps = workflow => {
    switch (workflow) {
      case 'newCandidate':
        return t.newCandidateSteps || [];
      case 'interviewScheduling':
        return t.interviewSteps || [];
      case 'documentVerification':
        return t.documentVerificationSteps || [];
      case 'followUp':
        return t.followUpSteps || [];
      default:
        return [];
    }
  };
  const getStatusBadgeClass = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'inProgress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getWorkflowMetrics = workflow => {
    switch (workflow) {
      case 'newCandidate':
        return {
          timePercentage: 75,
          accuracyPercentage: 92,
          satisfactionPercentage: 88
        };
      case 'interviewScheduling':
        return {
          timePercentage: 85,
          accuracyPercentage: 94,
          satisfactionPercentage: 90
        };
      case 'documentVerification':
        return {
          timePercentage: 70,
          accuracyPercentage: 96,
          satisfactionPercentage: 82
        };
      case 'followUp':
        return {
          timePercentage: 65,
          accuracyPercentage: 90,
          satisfactionPercentage: 85
        };
      default:
        return {
          timePercentage: 0,
          accuracyPercentage: 0,
          satisfactionPercentage: 0
        };
    }
  };
  const getWorkflowName = workflow => {
    switch (workflow) {
      case 'newCandidate':
        return t.newCandidate;
      case 'interviewScheduling':
        return t.interviewScheduling;
      case 'documentVerification':
        return t.documentVerification;
      case 'followUp':
        return t.followUp;
      default:
        return '';
    }
  };
  const toggleExpandStep = index => {
    if (expandedStep === index) {
      setExpandedStep(null);
    } else {
      setExpandedStep(index);
    }
  };
  const getAIAssistanceLevel = level => {
    switch (level) {
      case 'Very High':
        return <div className="flex items-center" data-id="element-397">
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-398"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-399"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-400"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-401"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-402"></div>
          </div>;
      case 'High':
        return <div className="flex items-center" data-id="element-403">
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-404"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-405"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-406"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-407"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-408"></div>
          </div>;
      case 'Medium':
        return <div className="flex items-center" data-id="element-409">
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-410"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-411"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-412"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300 mr-1" data-id="element-413"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-414"></div>
          </div>;
      case 'Low':
        return <div className="flex items-center" data-id="element-415">
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-416"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-417"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300 mr-1" data-id="element-418"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300 mr-1" data-id="element-419"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-420"></div>
          </div>;
      default:
        return <div className="flex items-center" data-id="element-421">
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-422"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300 mr-1" data-id="element-423"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300 mr-1" data-id="element-424"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300 mr-1" data-id="element-425"></div>
            <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-426"></div>
          </div>;
    }
  };
  const handleViewDetails = (step, index) => {
    setViewingStepDetails({
      ...step,
      index
    });
  };
  const handleEditStep = (step, index) => {
    setEditingStep({
      ...step,
      index
    });
    setEditFormData({
      ...step
    });
  };
  const handleCloseModal = () => {
    setViewingStepDetails(null);
    setEditingStep(null);
    setEditFormData(null);
  };
  const handleEditFormChange = (field, value) => {
    setEditFormData({
      ...editFormData,
      [field]: value
    });
  };
  const handleSaveEdit = () => {
    // In a real application, this would save the changes to the backend
    // For this demo, we'll just close the modal
    handleCloseModal();
    // Show a success message or notification here
  };
  // Modal component for both details and edit views
  const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md'
  }) => {
    if (!isOpen) return null;
    const sizeClasses = {
      sm: 'max-w-md',
      md: 'max-w-2xl',
      lg: 'max-w-4xl',
      xl: 'max-w-6xl'
    };
    return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-427">
        <div className={`bg-white rounded-lg shadow-xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col`} data-id="element-428">
          <div className="flex justify-between items-center border-b px-6 py-4" data-id="element-429">
            <h3 className="text-xl font-semibold text-gray-800" data-id="element-430">{title}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 focus:outline-none" data-id="element-431">
              <X size={20} data-id="element-432" />
            </button>
          </div>
          <div className="flex-grow overflow-y-auto p-6" data-id="element-433">{children}</div>
        </div>
      </div>;
  };
  // Step Details View
  const StepDetailsView = () => {
    if (!viewingStepDetails) return null;
    const step = viewingStepDetails;
    const runHistory = [{
      date: '2023-07-15',
      duration: '1m 42s',
      status: 'completed',
      user: 'System'
    }, {
      date: '2023-07-10',
      duration: '1m 55s',
      status: 'completed',
      user: 'System'
    }, {
      date: '2023-07-05',
      duration: '2m 03s',
      status: 'completed',
      user: 'System'
    }];
    return <Modal isOpen={!!viewingStepDetails} onClose={handleCloseModal} title={t.stepDetails.detailsTitle} size="lg" data-id="element-434">
        <div className="space-y-6" data-id="element-435">
          {/* Header with icon and status */}
          <div className="flex items-start" data-id="element-436">
            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center mr-4 ${step.status === 'completed' ? 'bg-green-100' : step.status === 'inProgress' ? 'bg-blue-100' : step.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'}`} data-id="element-437">
              {getStepIcon(step.icon, step.status)}
            </div>
            <div data-id="element-438">
              <h2 className="text-xl font-bold text-gray-800" data-id="element-439">{step.name}</h2>
              <div className="flex items-center mt-1" data-id="element-440">
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadgeClass(step.status)}`} data-id="element-441">
                  {t.stepStatus[step.status]}
                </span>
                <span className="text-sm text-gray-500 ml-3" data-id="element-442">
                  <Clock size={14} className="inline mr-1" data-id="element-443" />
                  {step.timeEstimate}
                </span>
              </div>
            </div>
          </div>
          {/* Description */}
          <div data-id="element-444">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-445">
              {t.stepDetails.description}
            </h3>
            <p className="text-gray-700 bg-gray-50 p-4 rounded-md" data-id="element-446">
              {step.description}
            </p>
          </div>
          {/* Details grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-447">
            <div className="bg-gray-50 p-4 rounded-md" data-id="element-448">
              <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-449">
                {t.stepDetails.aiAssistance}
              </h4>
              <div className="flex items-center" data-id="element-450">
                <Bot size={16} className="text-gray-700 mr-2" data-id="element-451" />
                <span className="font-medium" data-id="element-452">{step.aiAssistance}</span>
                <div className="ml-2" data-id="element-453">
                  {getAIAssistanceLevel(step.aiAssistance)}
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-md" data-id="element-454">
              <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-455">
                {t.stepDetails.integrations}
              </h4>
              <div className="flex items-center" data-id="element-456">
                <GitBranch size={16} className="text-gray-700 mr-2" data-id="element-457" />
                <span className="font-medium" data-id="element-458">{step.integration}</span>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-md" data-id="element-459">
              <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-460">
                {t.stepDetails.averageTime}
              </h4>
              <div className="flex items-center" data-id="element-461">
                <Clock size={16} className="text-gray-700 mr-2" data-id="element-462" />
                <span className="font-medium" data-id="element-463">{step.timeEstimate}</span>
              </div>
            </div>
          </div>
          {/* Previous runs */}
          <div data-id="element-464">
            <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-465">
              {t.stepDetails.previousRuns}
            </h3>
            <div className="border rounded-md overflow-hidden" data-id="element-466">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-467">
                <thead className="bg-gray-50" data-id="element-468">
                  <tr data-id="element-469">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-470">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-471">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-472">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-473">
                      Triggered By
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-474">
                  {runHistory.map((run, idx) => <tr key={idx} data-id="element-475">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-476">
                        {run.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-477">
                        {run.duration}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-478">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadgeClass(run.status)}`} data-id="element-479">
                          {t.stepStatus[run.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-480">
                        {run.user}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t" data-id="element-481">
            <button onClick={handleCloseModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium flex items-center" data-id="element-482">
              <ArrowLeft size={16} className="mr-2" data-id="element-483" />
              {t.stepDetails.back}
            </button>
            <button onClick={() => {
            handleCloseModal();
            handleEditStep(step, step.index);
          }} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium flex items-center" data-id="element-484">
              <Edit size={16} className="mr-2" data-id="element-485" />
              {t.stepDetails.edit}
            </button>
          </div>
        </div>
      </Modal>;
  };
  // Step Edit View
  const StepEditView = () => {
    if (!editingStep || !editFormData) return null;
    const statusOptions = ['completed', 'inProgress', 'pending', 'failed'];
    const aiAssistanceOptions = ['Very High', 'High', 'Medium', 'Low'];
    return <Modal isOpen={!!editingStep} onClose={handleCloseModal} title={t.stepDetails.editTitle} size="lg" data-id="element-486">
        <div className="space-y-6" data-id="element-487">
          {/* Name field */}
          <div data-id="element-488">
            <label htmlFor="step-name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-489">
              Name
            </label>
            <input type="text" id="step-name" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editFormData.name} onChange={e => handleEditFormChange('name', e.target.value)} data-id="element-490" />
          </div>
          {/* Description field */}
          <div data-id="element-491">
            <label htmlFor="step-description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-492">
              {t.stepDetails.description}
            </label>
            <textarea id="step-description" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editFormData.description} onChange={e => handleEditFormChange('description', e.target.value)} data-id="element-493" />
          </div>
          {/* Status and time estimate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-494">
            <div data-id="element-495">
              <label htmlFor="step-status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-496">
                {t.stepDetails.status}
              </label>
              <select id="step-status" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editFormData.status} onChange={e => handleEditFormChange('status', e.target.value)} data-id="element-497">
                {statusOptions.map(status => <option key={status} value={status} data-id="element-498">
                    {t.stepStatus[status]}
                  </option>)}
              </select>
            </div>
            <div data-id="element-499">
              <label htmlFor="step-time" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-500">
                {t.stepDetails.timeEstimate}
              </label>
              <input type="text" id="step-time" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editFormData.timeEstimate} onChange={e => handleEditFormChange('timeEstimate', e.target.value)} data-id="element-501" />
            </div>
          </div>
          {/* AI Assistance and Integration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-502">
            <div data-id="element-503">
              <label htmlFor="step-ai" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-504">
                {t.stepDetails.aiAssistance}
              </label>
              <select id="step-ai" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editFormData.aiAssistance} onChange={e => handleEditFormChange('aiAssistance', e.target.value)} data-id="element-505">
                {aiAssistanceOptions.map(level => <option key={level} value={level} data-id="element-506">
                    {level}
                  </option>)}
              </select>
            </div>
            <div data-id="element-507">
              <label htmlFor="step-integration" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-508">
                {t.stepDetails.integration}
              </label>
              <input type="text" id="step-integration" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editFormData.integration} onChange={e => handleEditFormChange('integration', e.target.value)} data-id="element-509" />
            </div>
          </div>
          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t" data-id="element-510">
            <button onClick={handleCloseModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium" data-id="element-511">
              {t.stepDetails.cancel}
            </button>
            <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium flex items-center" data-id="element-512">
              <Save size={16} className="mr-2" data-id="element-513" />
              {t.stepDetails.save}
            </button>
          </div>
        </div>
      </Modal>;
  };
  return <div className="container mx-auto px-4 py-6" data-id="element-514">
      <div className="bg-white rounded-lg shadow-lg p-6" data-id="element-515">
        <h2 className="text-2xl font-bold text-gray-800" data-id="element-516">{t.title}</h2>
        <p className="text-gray-600 mb-6" data-id="element-517">{t.subtitle}</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-518">
          {/* Workflow List */}
          <div className="lg:col-span-1" data-id="element-519">
            <h3 className="text-lg font-medium text-gray-700 mb-3" data-id="element-520">
              {t.workflows}
            </h3>
            <div className="space-y-3" data-id="element-521">
              {['newCandidate', 'interviewScheduling', 'documentVerification', 'followUp'].map(workflow => <div key={workflow} className={`p-4 border rounded-lg cursor-pointer transition-all ${activeWorkflow === workflow ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => {
              setActiveWorkflow(workflow);
              setExpandedStep(null);
            }} data-id="element-522">
                  <div className="flex items-center justify-between" data-id="element-523">
                    <div className="flex items-center space-x-3" data-id="element-524">
                      {getWorkflowIcon(workflow)}
                      <span className="font-medium" data-id="element-525">
                        {getWorkflowName(workflow)}
                      </span>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full ${workflowStatus[workflow] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-526">
                      {workflowStatus[workflow] ? t.status.active : t.status.inactive}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Workflow Details */}
          <div className="lg:col-span-2 border rounded-lg p-5" data-id="element-527">
            <div className="flex items-center justify-between mb-6" data-id="element-528">
              <div className="flex items-center space-x-3" data-id="element-529">
                {getWorkflowIcon(activeWorkflow)}
                <h3 className="text-xl font-medium" data-id="element-530">
                  {getWorkflowName(activeWorkflow)}
                </h3>
              </div>
              <div className="flex space-x-2" data-id="element-531">
                <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md" title={t.configure} data-id="element-532">
                  <Settings size={18} data-id="element-533" />
                </button>
                <button onClick={() => toggleWorkflowStatus(activeWorkflow)} className={`px-4 py-2 rounded-md text-sm font-medium ${workflowStatus[activeWorkflow] ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`} data-id="element-534">
                  {workflowStatus[activeWorkflow] ? t.deactivate : t.activate}
                </button>
              </div>
            </div>
            {/* Workflow Steps */}
            <div className="mb-6" data-id="element-535">
              <h4 className="text-md font-medium text-gray-700 mb-3" data-id="element-536">
                {t.steps}
              </h4>
              <div className="space-y-4" data-id="element-537">
                {getWorkflowSteps(activeWorkflow).map((step, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-538">
                    {/* Step Header */}
                    <div className={`p-3 cursor-pointer ${expandedStep === index ? 'bg-blue-50 border-b border-gray-200' : 'bg-gray-50'}`} onClick={() => toggleExpandStep(index)} data-id="element-539">
                      <div className="flex items-center justify-between" data-id="element-540">
                        <div className="flex items-center space-x-3" data-id="element-541">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${step.status === 'completed' ? 'bg-green-100' : step.status === 'inProgress' ? 'bg-blue-100' : step.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'}`} data-id="element-542">
                            {getStepIcon(step.icon, step.status)}
                          </div>
                          <div data-id="element-543">
                            <div className="font-medium text-gray-800" data-id="element-544">
                              {step.name}
                            </div>
                            <div className="flex items-center space-x-2 mt-1" data-id="element-545">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadgeClass(step.status)}`} data-id="element-546">
                                {t.stepStatus[step.status]}
                              </span>
                              <span className="text-xs text-gray-500" data-id="element-547">
                                {step.timeEstimate}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2" data-id="element-548">
                          {index < getWorkflowSteps(activeWorkflow).length - 1 && <div className="hidden md:block text-gray-400" data-id="element-549">
                              <ChevronRight size={18} data-id="element-550" />
                            </div>}
                          <div className={`transform transition-transform ${expandedStep === index ? 'rotate-180' : ''}`} data-id="element-551">
                            <ChevronDown size={16} className="text-gray-500" data-id="element-552" />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Step Details */}
                    {expandedStep === index && <div className="p-4 bg-white" data-id="element-553">
                        <p className="text-gray-700 mb-4" data-id="element-554">{step.description}</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-555">
                          <div className="bg-gray-50 p-3 rounded-md" data-id="element-556">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-557">
                              {t.stepDetails.timeEstimate}
                            </div>
                            <div className="flex items-center" data-id="element-558">
                              <Clock size={14} className="text-gray-500 mr-1" data-id="element-559" />
                              <span className="text-sm font-medium" data-id="element-560">
                                {step.timeEstimate}
                              </span>
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md" data-id="element-561">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-562">
                              {t.stepDetails.aiAssistance}
                            </div>
                            <div className="flex items-center" data-id="element-563">
                              <Bot size={14} className="text-gray-500 mr-1" data-id="element-564" />
                              <span className="text-sm font-medium mr-2" data-id="element-565">
                                {step.aiAssistance}
                              </span>
                              {getAIAssistanceLevel(step.aiAssistance)}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md" data-id="element-566">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-567">
                              {t.stepDetails.integration}
                            </div>
                            <div className="flex items-center" data-id="element-568">
                              <GitBranch size={14} className="text-gray-500 mr-1" data-id="element-569" />
                              <span className="text-sm font-medium" data-id="element-570">
                                {step.integration}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2" data-id="element-571">
                          <button className="text-xs flex items-center px-2 py-1 text-blue-600 hover:bg-blue-50 rounded" onClick={e => {
                      e.stopPropagation();
                      handleViewDetails(step, index);
                    }} data-id="element-572">
                            <Eye size={14} className="mr-1" data-id="element-573" />
                            {t.stepDetails.view}
                          </button>
                          <button className="text-xs flex items-center px-2 py-1 text-gray-600 hover:bg-gray-50 rounded" onClick={e => {
                      e.stopPropagation();
                      handleEditStep(step, index);
                    }} data-id="element-574">
                            <Edit size={14} className="mr-1" data-id="element-575" />
                            {t.stepDetails.edit}
                          </button>
                        </div>
                      </div>}
                  </div>)}
              </div>
            </div>
            {/* Performance Metrics */}
            <div data-id="element-576">
              <h4 className="text-md font-medium text-gray-700 mb-3" data-id="element-577">
                {t.metrics}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-578">
                <div className="p-4 bg-blue-50 rounded-lg" data-id="element-579">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-580">
                    <Clock size={18} className="text-blue-600" data-id="element-581" />
                    <span className="font-medium text-gray-700" data-id="element-582">
                      {t.avgTime}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-blue-700" data-id="element-583">
                    {getWorkflowMetrics(activeWorkflow).timePercentage}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2" data-id="element-584">
                    <div className="bg-blue-600 h-2 rounded-full" style={{
                    width: `${getWorkflowMetrics(activeWorkflow).timePercentage}%`
                  }} data-id="element-585"></div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg" data-id="element-586">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-587">
                    <CheckCircle size={18} className="text-green-600" data-id="element-588" />
                    <span className="font-medium text-gray-700" data-id="element-589">
                      {t.accuracy}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-green-700" data-id="element-590">
                    {getWorkflowMetrics(activeWorkflow).accuracyPercentage}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2" data-id="element-591">
                    <div className="bg-green-600 h-2 rounded-full" style={{
                    width: `${getWorkflowMetrics(activeWorkflow).accuracyPercentage}%`
                  }} data-id="element-592"></div>
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg" data-id="element-593">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-594">
                    <Users size={18} className="text-purple-600" data-id="element-595" />
                    <span className="font-medium text-gray-700" data-id="element-596">
                      {t.satisfaction}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-purple-700" data-id="element-597">
                    {getWorkflowMetrics(activeWorkflow).satisfactionPercentage}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2" data-id="element-598">
                    <div className="bg-purple-600 h-2 rounded-full" style={{
                    width: `${getWorkflowMetrics(activeWorkflow).satisfactionPercentage}%`
                  }} data-id="element-599"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modal components */}
      <StepDetailsView data-id="element-600" />
      <StepEditView data-id="element-601" />
    </div>;
}